import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, Medal, Crown, Flame, Users, Globe, 
  TrendingUp, Award, ChevronUp, ChevronDown, Minus
} from 'lucide-react';
import axios from 'axios';

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [filterType, setFilterType] = useState('global');
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [totalAthletes, setTotalAthletes] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [filterType]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/badges/leaderboard`, {
        params: { filter_type: filterType, limit: 50 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
      setCurrentUserRank(response.data.current_user_rank);
      setTotalAthletes(response.data.total_athletes || 0);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-aura-text-muted">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border-yellow-500/40';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-300/10 border-gray-400/40';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600/40';
      default:
        return 'bg-white/5 border-white/10';
    }
  };

  const getStreakBadge = (streak) => {
    if (streak >= 30) return { label: '🔥 30+ Day Streak', color: 'bg-orange-500/20 text-orange-400' };
    if (streak >= 14) return { label: '🔥 14+ Day Streak', color: 'bg-yellow-500/20 text-yellow-400' };
    if (streak >= 7) return { label: '🔥 7+ Day Streak', color: 'bg-amber-500/20 text-amber-400' };
    if (streak >= 3) return { label: `${streak} Day Streak`, color: 'bg-green-500/20 text-green-400' };
    return null;
  };

  return (
    <DashboardLayout>
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 mb-4">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight">
            Aura Leaderboard
          </h1>
          <p className="text-aura-text-muted mt-2">
            Consistency drives your score. See how you rank.
          </p>
        </div>

        {/* Filter Tabs */}
        <Tabs value={filterType} onValueChange={setFilterType} className="w-full">
          <TabsList className="w-full bg-aura-surface border border-white/10">
            <TabsTrigger 
              value="global" 
              className="flex-1 data-[state=active]:bg-aura-primary"
              data-testid="filter-global"
            >
              <Globe className="w-4 h-4 mr-2" />
              Global
            </TabsTrigger>
            <TabsTrigger 
              value="team" 
              className="flex-1 data-[state=active]:bg-aura-primary"
              data-testid="filter-team"
              disabled={!user?.org_id}
            >
              <Users className="w-4 h-4 mr-2" />
              My Team
            </TabsTrigger>
            <TabsTrigger 
              value="friends" 
              className="flex-1 data-[state=active]:bg-aura-primary"
              data-testid="filter-friends"
            >
              <Users className="w-4 h-4 mr-2" />
              Friends
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Current User Rank Card */}
        {currentUserRank && (
          <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/10 border-aura-primary/40">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-aura-primary flex items-center justify-center">
                    <span className="font-heading font-bold text-white text-lg">
                      #{currentUserRank.rank}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-aura-text-muted uppercase tracking-wider">Your Rank</p>
                    <p className="font-heading font-bold text-xl text-white">
                      {currentUserRank.name}
                    </p>
                    {currentUserRank.sport && (
                      <p className="text-sm text-aura-text-muted">{currentUserRank.sport}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-heading font-bold text-2xl text-aura-primary">
                    {currentUserRank.total_points.toLocaleString()}
                  </p>
                  <p className="text-xs text-aura-text-muted">Aura Points</p>
                  {currentUserRank.current_streak > 0 && (
                    <Badge className="mt-1 bg-orange-500/20 text-orange-400 border-0">
                      <Flame className="w-3 h-3 mr-1" />
                      {currentUserRank.current_streak} Day Streak
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-aura-secondary mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">{totalAthletes}</p>
              <p className="text-xs text-aura-text-muted uppercase">
                {filterType === 'global' ? 'Total Athletes' : 
                 filterType === 'team' ? 'Team Members' : 'Friends'}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Award className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-2xl text-white">
                {leaderboard[0]?.total_points?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-aura-text-muted uppercase">Top Score</p>
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard List */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Rankings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-aura-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-aura-text-muted">Loading leaderboard...</p>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-aura-text-subtle mx-auto mb-3" />
                <p className="text-aura-text-muted">
                  {filterType === 'friends' 
                    ? 'No friends to show. Join a team or have a supporter link to you!'
                    : filterType === 'team'
                    ? 'No team members found. Join an organization to see your team!'
                    : 'No athletes on the leaderboard yet.'}
                </p>
              </div>
            ) : (
              leaderboard.map((entry, index) => {
                const isCurrentUser = entry.user_id === user?.user_id;
                const streakBadge = getStreakBadge(entry.current_streak);
                
                return (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                      getRankBg(entry.rank)
                    } ${isCurrentUser ? 'ring-2 ring-aura-primary' : ''}`}
                    data-testid={`leaderboard-entry-${entry.rank}`}
                  >
                    {/* Rank */}
                    <div className="w-10 flex items-center justify-center flex-shrink-0">
                      {getRankIcon(entry.rank)}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium truncate ${isCurrentUser ? 'text-aura-primary' : 'text-white'}`}>
                          {entry.name}
                          {isCurrentUser && <span className="text-xs text-aura-text-muted ml-1">(You)</span>}
                        </p>
                        {streakBadge && (
                          <Badge className={`text-xs border-0 ${streakBadge.color}`}>
                            {streakBadge.label}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {entry.sport && (
                          <span className="text-xs text-aura-text-muted">{entry.sport}</span>
                        )}
                        {entry.position && (
                          <span className="text-xs text-aura-text-muted">• {entry.position}</span>
                        )}
                        <span className="text-xs text-aura-text-muted">
                          • {entry.badge_count} badges
                        </span>
                      </div>
                    </div>

                    {/* Points */}
                    <div className="text-right flex-shrink-0">
                      <p className={`font-heading font-bold text-lg ${
                        entry.rank === 1 ? 'text-yellow-400' :
                        entry.rank === 2 ? 'text-gray-300' :
                        entry.rank === 3 ? 'text-amber-600' :
                        'text-white'
                      }`}>
                        {entry.total_points.toLocaleString()}
                      </p>
                      <p className="text-xs text-aura-text-muted">pts</p>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Motivation Footer */}
        <div className="text-center py-4">
          <p className="text-aura-text-muted italic">
            "Aura is Earned, Not Given"
          </p>
          <p className="text-xs text-aura-text-subtle mt-1">
            Keep completing challenges and logging your training to climb the ranks!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LeaderboardPage;
